import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Form, Formik } from "formik";
import { memo, useCallback } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    usePatchOrganizationClientMutation,
    useUpdateClientEntityMutation,
} from "@api/organizationClient.api";
import { useScope } from "@state/scopeContext.lib";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import { ClientDetailsRow } from "../clientViewConfig";
import { NoteFormSchema, useNoteEditing } from "./useNoteEditing";
import { useNotePopoverClose } from "./useNotePopoverClose";
import { NoteTextarea } from "./NoteTextarea";
import styles from "./NoteCell.module.scss";

export const NoteCell = memo(
    ({
        row,
        getValue,
    }: CellContext<ViewTableFeatures, ClientDetailsRow, string | null>) => {
        const { organizationId } = useScope();
        const updateEntity = useUpdateClientEntityMutation();
        const patchClient = usePatchOrganizationClientMutation();

        const workspace = row.getParentRow()?.original ?? row.original;
        const note = getValue();

        const handleUpdate = useCallback(
            (payload: string) => {
                if (!organizationId) {
                    return;
                }

                if (row.original.type === "entity") {
                    const entityId = row.original.id;
                    updateEntity.mutate({
                        organizationId,
                        clientId: workspace.id,
                        entityId: Number(entityId),
                        payload: { note: payload },
                    });
                } else if (row.original.type === "workspace") {
                    patchClient.mutate({
                        organizationId,
                        clientId: workspace.id,
                        payload: { note: payload },
                    });
                }
            },
            [
                organizationId,
                row.original.type,
                row.original.id,
                workspace.id,
                updateEntity,
                patchClient,
            ],
        );

        const { closeAndBlur, setClose } = useNotePopoverClose();

        const {
            isOpened,
            setIsOpened,
            textareaRef,
            formRef,
            handleSubmit,
            handleBlur,
            handleFocus,
            handleKeyDown,
        } = useNoteEditing(note, handleUpdate, closeAndBlur);

        setClose(() => setIsOpened(false));

        return (
            <ViewTableCell captureClicks className={styles.container}>
                <Formik<NoteFormSchema>
                    initialValues={{ value: note ?? "" }}
                    onSubmit={handleSubmit}
                    enableReinitialize
                    innerRef={formRef}
                >
                    <Popover open={isOpened} onOpenChange={setIsOpened}>
                        <PopoverTrigger>
                            <EditableCellContent
                                isEmpty={!note}
                                truncationFullText={note ?? undefined}
                            >
                                {note}
                            </EditableCellContent>
                        </PopoverTrigger>
                        <PopoverContent
                            side="bottom"
                            align="start"
                            sideOffset={-32}
                            className={styles.popover}
                        >
                            <Form>
                                <NoteTextarea
                                    textareaRef={textareaRef}
                                    handleFocus={handleFocus}
                                    handleBlur={handleBlur}
                                    handleKeyDown={handleKeyDown}
                                    noteTextareaClass={styles.noteTextarea}
                                />
                            </Form>
                        </PopoverContent>
                    </Popover>
                </Formik>
            </ViewTableCell>
        );
    },
);

NoteCell.displayName = "NoteCell";
